import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import api, {
  setSession, clearSession, setUnauthorizedHandler, setSessionChangeHandler,
  getAuthToken, getRefreshToken,
} from '../api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getAuthToken());
  const [loading, setLoading] = useState(Boolean(getAuthToken() || getRefreshToken()));

  // Rensar lokalt utan att prata med servern.
  const endSession = useCallback(() => {
    clearSession();
    setToken(null);
    setUser(null);
  }, []);

  /** Utloggning aterkallar sessionen aven pa servern. */
  const logout = useCallback(async () => {
    const refreshToken = getRefreshToken();
    endSession();
    if (refreshToken) {
      // Lyckas det inte gar vi ut anda; token slutar galla av sig sjalv.
      api.post('/auth/logout', { refreshToken }).catch(() => {});
    }
  }, [endSession]);

  useEffect(() => {
    setUnauthorizedHandler(endSession);
    // Nar api.js fornyat token ska React folja med, annars ansluter
    // socketen med ett värde som inte langre galler.
    setSessionChangeHandler(setToken);
    return () => {
      setUnauthorizedHandler(null);
      setSessionChangeHandler(null);
    };
  }, [endSession]);

  useEffect(() => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    // Har atkomsttoken gatt ut svarar servern 401, och api.js byter in
    // fornyelsetoken och kor om anropet innan det landar har.
    api.get('/user/me')
      .then(res => { if (!cancelled) setUser(res.data); })
      .catch(() => { if (!cancelled) endSession(); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, endSession]);

  const login = useCallback(async (identifier, password) => {
    const res = await api.post('/auth/login', { identifier, password });
    setSession({ token: res.data.token, refreshToken: res.data.refreshToken });
    setToken(res.data.token);
    setUser(res.data.user);
  }, []);

  const register = useCallback(async (username, email, password) => {
    const res = await api.post('/auth/register', { username, email, password });
    setSession({ token: res.data.token, refreshToken: res.data.refreshToken });
    setToken(res.data.token);
    setUser(res.data.user);
  }, []);

  const refreshUser = useCallback(async () => {
    const res = await api.get('/user/me');
    setUser(res.data);
    return res.data;
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, setUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth måste användas inuti en AuthProvider');
  return context;
}

export default AuthContext;
